import { useEffect, useState } from "react";

import { ArrowBack, Block } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { toast } from "@rubi/ui";
import { matchIsValidTel } from "mui-tel-input";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { apiReportClient } from "@/api";
import ViewOrEditPhoneField, {
  PHONE_COUNTRY_OPTIONS,
} from "@/common/MultiStateFields/ViewOrEditPhoneField";
import ViewOrEditTextField from "@/common/MultiStateFields/ViewOrEditTextField";
import { RubiModal } from "@/common/RubiModal";
import { getServiceConfig } from "@/common/serviceConfigurations";
import { assignableAppPermissions } from "@/components/UserManagement/permissionTree";
import { Permission, UserPermission } from "@/components/UserManagement/types";
import { userPermissionChecks } from "@/components/UserManagement/utils";
import { useDevEnvironment } from "@/contexts/DevContext";
import { IOrgUser, IReturnedOrgUser } from "@/types";

export const emptyUser: IReturnedOrgUser = {
  badge_number: "",
  email: "",
  first_name: "",
  id: "",
  last_name: "",
  rank_short: "",
  rubi_org_id: "",
  stytch_user_id: "",
  has_temp_pw: true,
  contact_phone_number: "",
  contact_phone_extension: "",
  unit: "",
  internal_user_id: "",
};

export const initValidationGrid = {
  badge_number: true,
  email: true,
  first_name: true,
  last_name: true,
  rank_short: true,
  contact_phone_number: true,
  contact_phone_extension: true,
  unit: true,
  internal_user_id: true,
};

export const fieldValidationRules = (
  user,
  mode,
  updateOwnProfile,
  isUnitVisible = false,
  isInternalUserIdRequired = false,
) => {
  const internalUserIdValid =
    !isInternalUserIdRequired ||
    (typeof user?.internal_user_id === "string" &&
      user.internal_user_id.trim().length > 0);

  if (mode === "add" || (mode === "edit" && !updateOwnProfile)) {
    return {
      badge_number: true,
      email:
        typeof user?.email === "string" &&
        user.email.length > 0 &&
        user.email.includes("@"),
      first_name: true,
      last_name: true,
      rank_short: true,
      contact_phone_number: true,
      contact_phone_extension: true,
      unit: isUnitVisible
        ? typeof user?.unit === "string" && user.unit.length > 0
        : true,
      internal_user_id: internalUserIdValid,
    };
  }

  return {
    badge_number: typeof user?.badge_number === "string",
    email:
      typeof user?.email === "string" &&
      user.email.length > 0 &&
      user.email.includes("@"),
    first_name:
      typeof user?.first_name === "string" && user.first_name.length > 0,
    last_name: typeof user?.last_name === "string" && user.last_name.length > 0,
    rank_short:
      typeof user?.rank_short === "string" && user.rank_short.length > 0,
    contact_phone_number: user?.contact_phone_number
      ? typeof user?.contact_phone_number === "string" &&
        matchIsValidTel(user.contact_phone_number, {
          onlyCountries: PHONE_COUNTRY_OPTIONS,
        })
      : false,
    contact_phone_extension: user?.contact_phone_extension
      ? typeof user?.contact_phone_extension === "string" &&
        (user.contact_phone_extension.length === 0 ||
          (user.contact_phone_extension.length > 0 &&
            !isNaN(Number(user.contact_phone_extension))))
      : true,
    unit: isUnitVisible
      ? typeof user?.unit === "string" && user.unit.length > 0
      : true,
    internal_user_id: internalUserIdValid,
  };
};

export const fieldLabels: {
  [keyof in keyof Omit<IOrgUser, "id">]: string;
} = {
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email",
  contact_phone_number: "Telephone",
  contact_phone_extension: "Ext",
  badge_number: "Badge Number",
  rank_short: "Rank",
};

const generateEmailBodyRequestAppTrial = ({ appName }: { appName: string }) => {
  const text = `Dear Rubicon Team, \n\nI'm interested in exploring Rubicon's other apps and request a trial for ${appName}.\n\nThank you, \n[Your Name]\n[Your Agency]\n[Your Contact Information]\n`;

  return encodeURIComponent(text);
};

const PermissionSwitchWithText = ({
  permissionState,
  permissionLabel,
  permissionDescription,
  onClick,
  disabled,
  orgHasAppAccess,
}: {
  permissionState: boolean;
  permissionLabel: string;
  permissionDescription?: string;
  onClick: () => void;
  disabled?: boolean;
  orgHasAppAccess?: boolean;
}) => {
  const emailBody = generateEmailBodyRequestAppTrial({
    appName: permissionLabel,
  });
  const mailtoLink = `mailto:support@tryrubicon.com?subject=Request for Trial Subscription to ${permissionLabel}&body=${emailBody}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        margin: "4px 0",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={permissionState}
              onChange={onClick}
              color="primary"
              disabled={
                disabled ||
                (orgHasAppAccess === false && !permissionState) ||
                (orgHasAppAccess === false && permissionState)
              }
            />
          }
          label={permissionLabel}
          labelPlacement="end"
          style={{
            marginRight: "auto",
          }}
        />
        {permissionDescription && (
          <Typography
            variant="body2"
            style={{
              color: "gray",
              marginLeft: "42px",
              fontSize: "0.875rem",
              marginTop: "-4px",
            }}
          >
            {permissionDescription}
          </Typography>
        )}
      </div>
      {orgHasAppAccess === false && (
        <Typography
          variant="body2"
          style={{
            color: "gray",
            marginLeft: "auto",
            alignItems: "center",
            fontSize: "0.8rem",
          }}
        >
          Your service does not have an active subscription to this application.
          <br />
          Reach out to <a href={mailtoLink}>Rubicon</a> to request a trial{" "}
        </Typography>
      )}
    </div>
  );
};

// const ConfirmationModal = ({
//   isModalOpenArg,
//   setIsModalOpenArg,
//   updateAccess,
//   accessMessage,
// }: {
//   isModalOpenArg: boolean;
//   setIsModalOpenArg: (arg: boolean) => void;
//   updateAccess: () => void;
//   accessMessage: string;
// }) => {
//   return (
//     <RubiModal
//       isModalOpenArg={isModalOpenArg}
//       setIsModalOpenArg={setIsModalOpenArg}
//     >
//       <div
//         style={{
//           padding: "1rem",
//         }}
//       >
//         <Typography variant="h6">Confirmation</Typography>
//         <Typography variant="body1">
//           Are you sure you want to update access to this application? <br />
//           <br />
//           The user will be able to: <br />
//           <Typography
//             variant="body2"
//             style={{
//               fontWeight: "bold",
//             }}
//           >
//             {accessMessage}
//           </Typography>
//         </Typography>
//         <div
//           style={{
//             display: "flex",
//             marginTop: "1rem",
//             flexDirection: "row",
//             justifyContent: "flex-end",
//           }}
//         >
//           <Button
//             variant="contained"
//             color="primary"
//             onClick={() => {
//               setIsModalOpenArg(false);
//             }}
//             style={{ marginRight: "1rem" }}
//           >
//             Cancel
//           </Button>
//           <Button
//             variant="outlined"
//             color="primary"
//             onClick={() => {
//               updateAccess();
//               setIsModalOpenArg(false);
//             }}
//           >
//             Confirm
//           </Button>
//         </div>
//       </div>
//     </RubiModal>
//   );
// };

export const generateInformationGrid = (
  orgUser: IOrgUser,
  setOrgUser: (user: IOrgUser) => void,
  mode: "view" | "edit" | "add",
  fieldValidationGrid: {
    [key in keyof Omit<IOrgUser, "id">]: boolean;
  } = initValidationGrid,
  setOrgUserPermissions: (userPermissions: Permission[]) => void,
  setInitOrgUserPermissions: (userPermissions: Permission[]) => void,
  orgUserPermissions: Permission[],
  currentUserPermissions: Permission[],
  orgId: string,
  modifyingOwnProfile: boolean,
  sendInvite?: boolean,
  setSendInvite?: (value: boolean) => void,
  onReportAccessListClick?: () => void,
  isInternalUserIdRequired = false,
) => {
  const { isDevEnv } = useDevEnvironment();
  const { t } = useTranslation("common");

  const permissionChecks = userPermissionChecks(currentUserPermissions);

  useEffect(() => {
    if (orgUser.id) {
      apiReportClient
        .getUserPermissions(orgUser.id)
        .then((res) => {
          const userPermissions = res.permissions.map(
            (permission) => permission.title,
          );
          setOrgUserPermissions(userPermissions);
          setInitOrgUserPermissions(userPermissions);
        })
        .catch(console.error);
    }
  }, [orgUser.id]);

  const renderField = (key, label, isSmallField, isOptional, isDisabled) => {
    let gridSize = isSmallField ? 3 : 6;

    if (key === "contact_phone_number") {
      gridSize = 4;
      return (
        <Grid size={{ sm: gridSize }} key={key}>
          <ViewOrEditPhoneField
            label={label}
            value={orgUser[key] ?? ""}
            mode={mode}
            error={!fieldValidationGrid[key]}
            onChange={(value) => setOrgUser({ ...orgUser, [key]: value })}
            required={!isOptional}
            disabled={isDisabled}
          />
        </Grid>
      );
    }

    if (key === "contact_phone_extension") {
      gridSize = 2;
    }

    return (
      <Grid size={{ sm: gridSize }} key={key}>
        <ViewOrEditTextField
          label={label}
          value={orgUser[key] ?? ""}
          mode={mode}
          error={!fieldValidationGrid[key]}
          onChange={(e) => setOrgUser({ ...orgUser, [key]: e.target.value })}
          required={!isOptional}
          disabled={isDisabled}
        />
      </Grid>
    );
  };

  const renderFields = () => (
    <Grid container spacing={2}>
      {Object.entries(fieldLabels).map(([key, label]) => {
        const isSmallField = [
          "contact_phone_number",
          "contact_phone_extension",
        ].includes(key);
        let isOptional = ["contact_phone_extension"].includes(key);
        const isDisabled = ["email"].includes(key) && mode === "edit";

        // Only email is mandatory for adding a new user or editing a user as an admin
        if (mode === "add" || (mode === "edit" && !modifyingOwnProfile)) {
          isOptional = !(key === "email");
        }
        return renderField(
          key,
          label,
          isSmallField,
          isOptional,
          isDisabled ||
            (mode === "edit" &&
              !(permissionChecks.canModifyUsers || modifyingOwnProfile)),
        );
      })}
      {(() => {
        const organizationConfig = getServiceConfig(orgId ?? "NA", isDevEnv);
        const isDisabled =
          mode === "view" ||
          (mode === "edit" &&
            !(permissionChecks.canModifyUsers || modifyingOwnProfile));

        const units = organizationConfig?.units;
        if (!units || units.length === 0) {
          // Render a text field for the unit
          return (
            <Grid size={{ sm: 6 }} key="unit">
              <ViewOrEditTextField
                label="Unit"
                value={orgUser.unit ?? ""}
                mode={mode}
                onChange={(e) =>
                  setOrgUser({ ...orgUser, unit: e.target.value })
                }
                required={false}
                disabled={isDisabled}
              />
            </Grid>
          );
        }

        const currentLabel =
          units.find((u) => u.value === (orgUser.unit ?? ""))?.label ?? "";
        const isUnitRequired =
          mode === "add" || (mode === "edit" && !modifyingOwnProfile);
        return (
          <Grid size={{ sm: 6 }} key="unit">
            {mode === "view" ? (
              <ViewOrEditTextField
                label="Unit"
                value={currentLabel}
                mode={"view"}
              />
            ) : (
              <FormControl
                fullWidth
                margin="normal"
                error={!fieldValidationGrid.unit}
                required={isUnitRequired}
                variant="outlined"
              >
                <InputLabel id="unit-select-label">Unit</InputLabel>
                <Select
                  labelId="unit-select-label"
                  value={orgUser.unit ?? ""}
                  label="Unit"
                  onChange={(e) =>
                    setOrgUser({ ...orgUser, unit: e.target.value as string })
                  }
                  disabled={isDisabled}
                >
                  {!isUnitRequired && (
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                  )}
                  {units.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
        );
      })()}
      {isInternalUserIdRequired && (
        <Grid size={{ sm: 6 }} key="internal_user_id">
          <ViewOrEditTextField
            label="Internal User ID"
            value={orgUser.internal_user_id ?? ""}
            mode={mode}
            error={!fieldValidationGrid.internal_user_id}
            onChange={(e) =>
              setOrgUser({ ...orgUser, internal_user_id: e.target.value })
            }
            required
            disabled={
              mode === "edit" &&
              !(permissionChecks.canModifyUsers || modifyingOwnProfile)
            }
          />
        </Grid>
      )}
    </Grid>
  );

  const permissionSwitchComponent = (
    userPermission: UserPermission,
    sectionType: "app" | "admin",
    organizationApps: string[],
    disabled: boolean,
    mainAppPermission?: UserPermission,
  ) => {
    const { permission, labelKey, descriptionKey, subPermissions } =
      userPermission;

    const currentState = orgUserPermissions.includes(permission);
    return (
      <>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <PermissionSwitchWithText
              key={permission}
              permissionState={currentState}
              permissionLabel={t(labelKey)}
              permissionDescription={descriptionKey && t(descriptionKey)}
              onClick={() => {
                if (orgUserPermissions.includes(permission)) {
                  let removed = orgUserPermissions.filter(
                    (p) => p !== permission,
                  );
                  // Disabling View Reports also disables View Individual Report
                  if (permission === Permission.REPORT_V2_VIEW_REPORTS) {
                    removed = removed.filter(
                      (p) => p !== Permission.REPORT_V2_VIEW_INDIVIDUAL_REPORT,
                    );
                  }
                  setOrgUserPermissions(removed);
                } else {
                  const added = [...orgUserPermissions, permission];
                  // Enabling View Reports also enables View Individual Report
                  if (
                    permission === Permission.REPORT_V2_VIEW_REPORTS &&
                    !added.includes(Permission.REPORT_V2_VIEW_INDIVIDUAL_REPORT)
                  ) {
                    added.push(Permission.REPORT_V2_VIEW_INDIVIDUAL_REPORT);
                  }
                  setOrgUserPermissions(added);
                }
              }}
              disabled={disabled}
              orgHasAppAccess={
                sectionType === "app"
                  ? organizationApps?.includes(permission) ||
                    organizationApps?.includes(
                      mainAppPermission?.permission ?? "",
                    )
                  : true
              }
            />
          </div>
          {(permission === Permission.REPORT_V2_VIEW_REPORTS ||
            permission === Permission.REGISTRY_VIEW_ENTRIES) &&
            currentState &&
            onReportAccessListClick && (
              <Button
                variant="outlined"
                size="small"
                onClick={onReportAccessListClick}
                style={{ marginRight: "8px", whiteSpace: "nowrap" }}
              >
                Report Access List
              </Button>
            )}
        </div>
        <div style={{ marginLeft: "16px" }}>
          {orgUserPermissions.includes(permission) &&
            subPermissions?.map((permission) =>
              permissionSwitchComponent(
                permission,
                sectionType,
                organizationApps,
                disabled,
                mainAppPermission || userPermission,
              ),
            )}
        </div>
      </>
    );
  };

  const renderPermissionsSection = (sectionType: "app" | "admin") => {
    const organizationConfig = getServiceConfig(orgId ?? "NA", isDevEnv);

    const { apps: organizationApps } = organizationConfig;

    const permissionsConfig = {
      app: assignableAppPermissions(organizationApps ?? []),
      admin: [
        {
          permission: Permission.RUBI_ORG_OWN_ADD_USER,
          labelKey: "userManagement.permissions.admin.label",
        },
      ],
    };

    const permissionsToRender = permissionsConfig[sectionType];

    const userHasPersmissionToUpdatePermissions =
      sectionType === "app"
        ? permissionChecks.canManageApps
        : permissionChecks.canManageAdmins;

    const disableSwitches =
      modifyingOwnProfile ||
      mode === "view" ||
      !userHasPersmissionToUpdatePermissions;

    return (
      <Grid>
        <Typography variant="h6">
          {sectionType === "app"
            ? "Application Access and Permissions"
            : "Admin Access and Permissions"}
        </Typography>
        {permissionsToRender.map((permission) =>
          permissionSwitchComponent(
            permission,
            sectionType,
            organizationApps,
            disableSwitches,
          ),
        )}
      </Grid>
    );
  };

  const renderSendInviteCheckbox = () => {
    if (mode !== "add" || !setSendInvite) {
      return null;
    }

    return (
      <Grid container spacing={2} style={{ marginBottom: "1rem" }}>
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendInvite || false}
                onChange={(e) => setSendInvite(e.target.checked)}
                color="primary"
              />
            }
            label="Send Invite"
          />
          <Typography variant="body2" color="textSecondary">
            If your organization uses Single Sign-On, you likely want to leave
            this box unchecked
          </Typography>
        </Grid>
      </Grid>
    );
  };

  return (
    <div style={{ marginBottom: "1rem" }}>
      {renderFields()}
      {renderSendInviteCheckbox()}
      {mode && renderPermissionsSection("app")}
      {mode && renderPermissionsSection("admin")}
    </div>
  );
};

export const BackToUsersListButton = (orgId: string) => {
  const navigate = useNavigate();
  return (
    <Button
      variant="contained"
      onClick={() => {
        navigate(`/organizations/${orgId}/users`);
      }}
      startIcon={<ArrowBack />}
    >
      Back to Users List
    </Button>
  );
};

export const DeactivateUserButton = ({
  userData,
  userHasLoginPermission,
  adminHasEditPermission,
}: {
  userData: IOrgUser;
  userHasLoginPermission: boolean;
  adminHasEditPermission: boolean;
}) => {
  const [deactivateUserDialogOpen, setDeactivateUserDialogOpen] =
    useState(false);

  const navigate = useNavigate();

  const deactivateUser = (userId) => {
    apiReportClient
      .deactivateOrgUser(userId)
      .then((response) => {
        toast.success("User deactivated successfully");
      })
      .catch((error) => {
        toast.error(`Error deactivating user: ${error}`);
      });
  };

  const DeactivateUserModal = () => {
    return (
      <RubiModal
        isModalOpenArg={deactivateUserDialogOpen}
        setIsModalOpenArg={setDeactivateUserDialogOpen}
        onClose={() => setDeactivateUserDialogOpen(false)}
      >
        <div
          style={{
            padding: "1rem",
          }}
        >
          <Typography variant="h6">Deactivate User</Typography>
          <Typography variant="body1">
            Are you sure you want to deactivate this user? They will no longer
            be able to log in to the system.
          </Typography>
          <div
            style={{
              display: "flex",
              marginTop: "1rem",
              flexDirection: "row",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setDeactivateUserDialogOpen(false);
              }}
              style={{ marginRight: "1rem" }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                deactivateUser(userData?.id);
                setDeactivateUserDialogOpen(false);
                navigate(`/organizations/${userData.rubi_org_id}/users`);
              }}
            >
              Deactivate
            </Button>
          </div>
        </div>
      </RubiModal>
    );
  };

  if (!adminHasEditPermission) {
    return null;
  }

  return (
    <>
      <Button
        startIcon={<Block />}
        variant="outlined"
        color="primary"
        size="medium"
        onClick={() => setDeactivateUserDialogOpen(true)}
        disabled={!userHasLoginPermission}
      >
        Deactivate
      </Button>
      <DeactivateUserModal />
    </>
  );
};
